/**
 * Sentry noise filter for the browser SDK.
 *
 * Browser extensions and other injected scripts throw on our pages and the
 * global error handler captures them as if they were our bugs — e.g.
 * SNAPTRANSFER-WEB-S: an unguarded `undefined.click()` from `<anonymous>:1`
 * (with a "Target element not found." console breadcrumb) on /requests, where
 * none of our own code does an unguarded `.click()`. `denyUrls` misses these
 * because the frames carry no URL — just `<anonymous>`.
 *
 * Our real app errors always have at least one frame from a bundled chunk
 * (`app:///_next/...`, `https://…/_next/static/...`). So an event whose frames
 * are *exclusively* non-first-party (anonymous / native / extension) is noise
 * we didn't cause and can't fix — drop it. Anything with even one first-party
 * frame is kept, so a real error that merely brushed an extension still reports.
 */

interface FrameLike {
  filename?: string
}
interface ExceptionValueLike {
  stacktrace?: { frames?: FrameLike[] }
}
export interface EventLike {
  exception?: { values?: ExceptionValueLike[] }
}

const EXTENSION_SCHEME = /^(chrome|moz|safari|webkit|ms-browser)-extension:\/\//

/** A frame that did not originate in our shipped bundle. */
function isThirdPartyFrame(filename: string | undefined): boolean {
  if (!filename) return true
  const f = filename.trim()
  if (f === '' || f === '<anonymous>' || f === '[native code]') return true
  return EXTENSION_SCHEME.test(f)
}

/**
 * True when the event has stack frames and every one of them is non-first-party.
 * False for first-party (or mixed) errors and for frameless events (messages),
 * which must always be delivered.
 */
export function isExclusivelyThirdPartyEvent(event: EventLike): boolean {
  const frames = (event.exception?.values ?? []).flatMap((v) => v.stacktrace?.frames ?? [])
  if (frames.length === 0) return false
  return frames.every((frame) => isThirdPartyFrame(frame.filename))
}
